// Copyright (c) 2026 SoundDrift. All rights reserved.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import SoundAvatar from './SoundAvatar.jsx';
import useClipLookup from './useClipLookup.js';
import '../../styles/theme.css';

/**
 * WinningSoundsReveal — Joins all winning sounds at the end of a game
 * merged together for the big team reveal.
 *
 * @param {Object} props
 * @param {Object} props.room Current game room state
 * @param {Object} props.audioEngine AudioEngine instance (from useAudioEngine)
 * @param {Array}  [props.customClips] Optional explicit list of clip objects/IDs to merge
 * @param {string} [props.title] Custom title
 * @param {boolean} [props.autoPlay=false] Whether to trigger automatically on mount
 */
export function WinningSoundsReveal({
  room = {},
  audioEngine = null,
  customClips = null,
  title = '🏆 Big Team Reveal: Winning Sounds Merged',
  autoPlay = false,
}) {
  const lookupClip = useClipLookup();
  const [isPlaying, setIsPlaying] = useState(false);
  const [playMode, setPlayMode] = useState('merged'); // 'merged' | 'sequence'
  const [activeClipIndex, setActiveClipIndex] = useState(-1);
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(() => {
    try {
      return localStorage.getItem('sounddrift_team_reveal_autoplay') === 'true';
    } catch {
      return false;
    }
  });

  const stopHandleRef = useRef(null);

  // Extract winning / featured clips from current mode & players
  const extractFeaturedClips = useCallback(() => {
    if (customClips && Array.isArray(customClips) && customClips.length > 0) {
      return customClips.map((c) => (typeof c === 'string' ? lookupClip(c) || { id: c, label: c } : c)).filter(Boolean);
    }

    const modeState = room?.modeState || {};
    const players = Object.values(room?.players || {}).filter((p) => !p.isHost);
    const sortedPlayers = players.slice().sort((a, b) => (b.score || 0) - (a.score || 0));
    const winner = sortedPlayers[0];

    const collected = [];
    const seenIds = new Set();

    const addClip = (raw, meta = {}) => {
      if (!raw) return;
      const clip = typeof raw === 'string' ? lookupClip(raw) || { id: raw, label: raw } : raw;
      if (clip && clip.id && !seenIds.has(clip.id)) {
        seenIds.add(clip.id);
        collected.push({ ...clip, ...meta });
      }
    };

    // 1. Top player's signature sound
    if (winner && winner.avatar) {
      addClip(winner.avatar, { playerName: winner.name, isWinner: true, badge: 'Winner Signature' });
    }

    // 2. Mode-specific winning submissions
    if (modeState.duels && Array.isArray(modeState.duels)) {
      // Sound Off duels
      modeState.duels.forEach((duel, idx) => {
        const sub = modeState.submissions?.[duel.winnerId || duel.duelistAId];
        if (sub?.clipIds) {
          sub.clipIds.forEach((cId, cIdx) => addClip(cId, { badge: `Duel ${idx + 1} Clip ${cIdx + 1}` }));
        }
      });
    }

    if (modeState.mysteryClip) {
      // Name That Noise mystery sound
      addClip(modeState.mysteryClip, { badge: 'Mystery Sound Truth' });
    }

    if (modeState.chains && Array.isArray(modeState.chains)) {
      // Drift audio steps
      modeState.chains.forEach((chain, cIdx) => {
        if (chain.steps) {
          chain.steps.forEach((step, sIdx) => {
            if (step.audioUrl || step.clipId) {
              addClip(step.audioUrl || step.clipId, { badge: `Chain ${cIdx + 1} Step ${sIdx + 1}` });
            }
          });
        }
      });
    }

    if (modeState.submissions) {
      // Foley 3-clip productions or other submissions
      Object.entries(modeState.submissions).forEach(([pId, sub]) => {
        if (sub.clips && Array.isArray(sub.clips)) {
          sub.clips.forEach((c, i) => addClip(c.clipId || c.id || c, { badge: `Scene Sound ${i + 1}` }));
        } else if (sub.clipIds && Array.isArray(sub.clipIds)) {
          sub.clipIds.forEach((cId, i) => addClip(cId, { badge: `Answer Sound ${i + 1}` }));
        }
      });
    }

    // 3. Fallback: all connected players' signature sounds
    if (collected.length < 2) {
      sortedPlayers.forEach((p) => {
        if (p.avatar) addClip(p.avatar, { playerName: p.name, badge: `${p.name}'s Sound` });
      });
    }

    // 4. Demo fallback clips for studio / preview testing
    if (collected.length === 0) {
      ['laser_tag', 'arcade_jump', 'fart_reverb', 'sonar_ping'].forEach((id, i) => {
        addClip(id, { badge: `Featured Sound #${i + 1}`, label: id.replace(/_/g, ' ') });
      });
    }

    return collected.slice(0, 8);
  }, [customClips, lookupClip, room]);

  const clips = extractFeaturedClips();

  const handleStop = useCallback(() => {
    if (stopHandleRef.current && typeof stopHandleRef.current.stop === 'function') {
      try {
        stopHandleRef.current.stop();
      } catch {}
    }
    setIsPlaying(false);
    setActiveClipIndex(-1);
  }, []);

  const handlePlayMerged = useCallback(async () => {
    if (!audioEngine || clips.length === 0) return;
    handleStop();
    setIsPlaying(true);

    try {
      if (audioEngine.unlock) await audioEngine.unlock();

      if (playMode === 'merged') {
        // Layered Fanfare: Stagger clips by 0.18s with balanced gain
        const items = clips.map((c, i) => ({
          clipId: c.id,
          delay: i === 0 ? 0 : 0.18,
          gain: Math.max(0.4, 0.9 - i * 0.06),
        }));

        if (audioEngine.playSequence) {
          const handle = await audioEngine.playSequence(items, {
            onEnded: () => {
              setIsPlaying(false);
              setActiveClipIndex(-1);
            },
          });
          stopHandleRef.current = handle;
        }
      } else {
        // Sequential Megamix: Play back-to-back with 0.1s gap
        const items = clips.map((c) => ({
          clipId: c.id,
          gap: 0.1,
          gain: 0.95,
        }));

        if (audioEngine.playSequence) {
          const handle = await audioEngine.playSequence(items, {
            onProgress: ({ itemIndex }) => setActiveClipIndex(itemIndex),
            onEnded: () => {
              setIsPlaying(false);
              setActiveClipIndex(-1);
            },
          });
          stopHandleRef.current = handle;
        }
      }
    } catch (e) {
      console.warn('[WinningSoundsReveal] Playback error:', e);
      setIsPlaying(false);
    }
  }, [audioEngine, clips, handleStop, playMode]);

  // Auto-play on mount if option is enabled
  useEffect(() => {
    if (autoPlay || autoPlayEnabled) {
      const timer = setTimeout(() => {
        handlePlayMerged();
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [autoPlay, autoPlayEnabled, handlePlayMerged]);

  useEffect(() => {
    return () => handleStop();
  }, [handleStop]);

  if (clips.length === 0) {
    return null;
  }

  return (
    <div
      data-testid="winning-sounds-reveal"
      style={{
        background: 'linear-gradient(135deg, rgba(224, 164, 88, 0.08) 0%, rgba(58, 207, 207, 0.06) 100%), var(--sd-panel, #0D1015)',
        border: '1px solid var(--sd-amber, #E0A458)',
        borderRadius: '20px',
        padding: '24px 28px',
        marginTop: '24px',
        boxShadow: isPlaying ? '0 0 32px rgba(224, 164, 88, 0.3), 0 8px 24px rgba(0, 0, 0, 0.5)' : '0 4px 20px rgba(0, 0, 0, 0.4)',
        transition: 'all 0.3s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px', marginBottom: '20px' }}>
        <div>
          <div
            style={{
              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              fontSize: '11.5px',
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: 'var(--sd-amber, #E0A458)',
              fontWeight: 700,
            }}
          >
            Big Team Reveal
          </div>
          <h3
            style={{
              fontSize: 'clamp(1.3rem, 1.8vw, 1.7rem)',
              fontWeight: 800,
              color: 'var(--sd-ink, #EDEFF2)',
              margin: '4px 0 0 0',
              letterSpacing: '-0.02em',
            }}
          >
            {title}
          </h3>
        </div>

        {/* Controls & Mode Switcher */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <div
            style={{
              display: 'flex',
              background: 'var(--sd-ground, #08090B)',
              border: '1px solid var(--sd-line-2, #1C2229)',
              borderRadius: '10px',
              padding: '2px',
            }}
          >
            <button
              type="button"
              data-testid="reveal-mode-merged"
              onClick={() => setPlayMode('merged')}
              style={{
                background: playMode === 'merged' ? 'var(--sd-amber, #E0A458)' : 'transparent',
                color: playMode === 'merged' ? '#08090B' : 'var(--sd-ink-2, #C6CDD4)',
                border: 'none',
                borderRadius: '8px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              Layered Symphony
            </button>
            <button
              type="button"
              data-testid="reveal-mode-sequence"
              onClick={() => setPlayMode('sequence')}
              style={{
                background: playMode === 'sequence' ? 'var(--sd-amber, #E0A458)' : 'transparent',
                color: playMode === 'sequence' ? '#08090B' : 'var(--sd-ink-2, #C6CDD4)',
                border: 'none',
                borderRadius: '8px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              Sequential Megamix
            </button>
          </div>

          {isPlaying ? (
            <button
              type="button"
              data-testid="stop-winning-reveal-btn"
              onClick={handleStop}
              style={{
                minHeight: '44px',
                padding: '0 20px',
                borderRadius: '12px',
                background: 'var(--sd-rose, #E4738A)',
                color: '#08090B',
                border: 'none',
                fontWeight: 800,
                fontSize: '14px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span>⏹</span>
              <span>Stop Audio</span>
            </button>
          ) : (
            <button
              type="button"
              data-testid="play-winning-reveal-btn"
              onClick={handlePlayMerged}
              style={{
                minHeight: '44px',
                padding: '0 24px',
                borderRadius: '12px',
                background: 'var(--sd-amber, #E0A458)',
                color: '#08090B',
                border: 'none',
                fontWeight: 800,
                fontSize: '14.5px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 0 16px rgba(224, 164, 88, 0.4)',
                transition: 'transform 0.15s ease',
              }}
            >
              <span>🎉</span>
              <span>Play Winning Sounds Merged</span>
            </button>
          )}
        </div>
      </div>

      {/* Featured Clips Grid */}
      <div
        data-testid="winning-clips-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
        }}
      >
        {clips.map((clip, i) => {
          const isActive = isPlaying && (playMode === 'merged' || activeClipIndex === i);
          return (
            <div
              key={clip.id || i}
              data-testid={`winning-clip-card-${i}`}
              style={{
                background: isActive ? 'rgba(224, 164, 88, 0.15)' : 'var(--sd-panel-2, #0E1116)',
                border: `1px solid ${isActive ? 'var(--sd-amber, #E0A458)' : 'var(--sd-line-2, #1C2229)'}`,
                borderRadius: '12px',
                padding: '12px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                transform: isActive ? 'scale(1.02)' : 'scale(1)',
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span
                  style={{
                    fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                    fontSize: '10.5px',
                    color: isActive ? 'var(--sd-amber, #E0A458)' : 'var(--sd-dim, #6E7982)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                  }}
                >
                  {clip.badge || `Sound #${i + 1}`}
                </span>
                {isActive && (
                  <span style={{ fontSize: '12px', animation: 'spin 1.5s linear infinite' }}>
                    🎵
                  </span>
                )}
              </div>
              <SoundAvatar clip={clip} seed={clip.label || clip.id} size="sm" />
              <div
                style={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: 'var(--sd-ink, #EDEFF2)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {clip.label || clip.id}
              </div>
            </div>
          );
        })}
      </div>

      {/* Optional Auto-Play Checkbox */}
      <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input
          type="checkbox"
          id="team-reveal-autoplay"
          data-testid="team-reveal-autoplay-checkbox"
          checked={autoPlayEnabled}
          onChange={(e) => {
            const checked = e.target.checked;
            setAutoPlayEnabled(checked);
            try {
              localStorage.setItem('sounddrift_team_reveal_autoplay', String(checked));
            } catch {}
          }}
          style={{ cursor: 'pointer', accentColor: 'var(--sd-amber, #E0A458)' }}
        />
        <label
          htmlFor="team-reveal-autoplay"
          style={{
            fontSize: '12.5px',
            color: 'var(--sd-muted, #79838D)',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          Always auto-play merged winning sound symphony when game finishes
        </label>
      </div>
    </div>
  );
}

export default WinningSoundsReveal;
